import React, { createContext, useCallback, useContext, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { agencyApiEvents } from 'modules/api/agency'
import { apiStateSelectors } from 'modules/api/apiState'
import { uiEvents } from 'modules/ui'
import { getSegmentsWithoutDefault } from './state/selectors'
import { TAgencySegment } from 'modules/api/agency/types'
import { TSegmentsFilter } from './types'

type TSegmentsContextValue = {
  segments: TAgencySegment[]
  filters: TSegmentsFilter[]
  redirect: (path: string) => void
  isLoading: boolean
}

const SegmentsContext = createContext<TSegmentsContextValue | null>(null)

export const SegmentsProvider = ({ children }: { children: React.ReactNode }): React.ReactElement => {
  const dispatch = useDispatch()
  const segments = useSelector(getSegmentsWithoutDefault)

  const filters = useMemo(() => [{ key: 'all', label: 'All', count: segments.length }], [segments])

  const redirect = useCallback((path: string) => dispatch(uiEvents.redirect(path)), [dispatch])

  // Only the first load replaces the listing with a loader; refetches (e.g. opening the drawer) keep it on screen
  const isFetching = useSelector(state => apiStateSelectors.isLoading(state)([agencyApiEvents.getSegments_request]))
  const isLoading = isFetching && segments.length === 0

  const value = useMemo(() => ({ segments, filters, redirect, isLoading }), [segments, filters, redirect, isLoading])

  return <SegmentsContext.Provider value={value}>{children}</SegmentsContext.Provider>
}

export const useSegments = (): TSegmentsContextValue => {
  const context = useContext(SegmentsContext)
  if (context === null) {
    throw new Error('useSegments must be used within a SegmentsProvider')
  }
  return context
}

export default SegmentsProvider
